import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Button, DatePicker, SearchableDropdown, SideBarModal } from '@/components';
import {
  Incident,
  IncidentSeverity,
  UpdateIncidentRequest,
  getIncidentById,
  updateIncident,
} from '@/services/api/incidents';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { v4 as uuidv4 } from 'uuid';
import { FileUploadField, UploadedFileInfo } from './ReportIncidentModal';
import {
  extractApiErrorMessage,
  getBucketUrlFile,
  getBucketUrlImageV2,
  uploadFileImage,
  uploadTempFile,
  getUserEmail,
} from '@/utils';
import dayjs from 'dayjs';

interface EditIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue: Incident | null;
  onSuccess?: () => void;
}

const INCIDENT_TYPE_MAP: Record<string, string> = {
  ACCIDENT_DAMAGE: 'Kecelakaan',
  USAGE_DAMAGE: 'Kerusakan akibat Penggunaan',
  LOST_UNIT: 'Kehilangan Unit',
  MINOR_DAMAGE: 'Kerusakan Ringan',
};

const severityOptions = [
  { value: IncidentSeverity.LOW, label: 'Low' },
  { value: IncidentSeverity.MEDIUM, label: 'Medium' },
  { value: IncidentSeverity.HIGH, label: 'High' },
  { value: IncidentSeverity.CRITICAL, label: 'Critical' },
];

const costBearerOptions = [
  { value: 'MITRA', label: 'Mitra' },
  { value: 'CLIENT', label: 'Client' },
  { value: 'OEM', label: 'OEM' },
  { value: 'DASH_ELECTRIC', label: 'Dash' },
];

const EXISTING_IMAGE_FILE_NAME = 'Current_Image.jpg';
const EXISTING_POLICE_REPORT_FILE_NAME = 'Current_Police_Report.pdf';

const createPlaceholderFile = (filename: string, type: string) =>
  new File([new ArrayBuffer(120 * 1024)], filename, { type });

interface EditForm {
  incidentDate: string;
  severity: IncidentSeverity;
  costBearer: string;
  chronology: string;
  photos: UploadedFileInfo[];
  policeReports: UploadedFileInfo[];
  imageURL: string;
  policeReportURL: string;
}

const defaultEditForm = (incident: Incident): EditForm => ({
  incidentDate: incident.incidentDate || '',
  severity: incident.severity || IncidentSeverity.LOW,
  costBearer: incident.costResponsibility || '',
  chronology: incident.chronology || incident.notes || '',
  photos: (incident.imageURL || incident.damageImageURL)
    ? [{ file: createPlaceholderFile(EXISTING_IMAGE_FILE_NAME, 'image/jpeg') }]
    : [],
  policeReports: incident.policeReportURL
    ? [{ file: createPlaceholderFile(EXISTING_POLICE_REPORT_FILE_NAME, 'application/pdf') }]
    : [],
  imageURL: incident.imageURL || incident.damageImageURL || '',
  policeReportURL: incident.policeReportURL || '',
});
const INCIDENT_IMAGE_TYPE = 'incident';

// ── Utility UI components ────────────────────────────────────────────────────

const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
  hasBorder?: boolean;
}> = ({ label, value, className = '', hasBorder = true }) => (
  <div className={`pb-2 ${className}`}>
    <label className="block mb-1 heading-field">{label}</label>
    <div
      className={`text-sm font-medium text-gray-900 break-words pb-3 ${
        hasBorder ? 'border-b border-gray-200' : ''
      }`}
    >
      {value || '-'}
    </div>
  </div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-1 mb-4">
    <span className="font-medium rounded-t-lg heading-field tracking-wider">
      {title}
    </span>
  </div>
);

const InfoSection: React.FC<{ issue: Incident }> = ({ issue }) => (
  <>
    <SectionHeader title="Info" />
    <div className="p-6 pt-0">
      <div className="pb-2 mb-2">
        <div className="grid grid-cols-2 gap-4 pb-3 border-b border-gray-200">
          <div>
            <label className="block mb-1 heading-field">Created By</label>
            <div className="text-sm font-medium text-gray-900 break-words">
              {issue.createdBy || '-'}
            </div>
          </div>
          <div>
            <label className="block mb-1 heading-field">Created Date</label>
            <div className="text-sm font-medium text-gray-900 break-words">
              {dayjs(issue.createdAt).format('MMMM D, YYYY [at] HH.mm')}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="grid grid-cols-2 gap-4 pb-3">
          <div>
            <label className="block mb-1 heading-field">Last Updated By</label>
            <div className="text-sm font-medium text-gray-900 break-words">
              {issue.updatedBy || '-'}
            </div>
          </div>
          <div>
            <label className="block mb-1 heading-field">Last Updated</label>
            <div className="text-sm font-medium text-gray-900 break-words">
              {issue.updatedAt
                ? dayjs(issue.updatedAt).format('MMMM D, YYYY [at] HH.mm')
                : '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
);

// ── Main modal component ─────────────────────────────────────────────────────

export const EditIncidentModal: React.FC<EditIncidentModalProps> = ({
  isOpen,
  onClose,
  issue,
  onSuccess,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [incidentDetail, setIncidentDetail] = useState<Incident | null>(null);

  const handleModalClose = () => {
    setIsEditing(false);
    onClose();
  };

  // ── edit form state ──
  const [formData, setFormData] = useState<EditForm>({
    incidentDate: '',
    severity: IncidentSeverity.LOW,
    costBearer: '',
    chronology: '',
    photos: [],
    policeReports: [],
    imageURL: '',
    policeReportURL: '',
  });

  useEffect(() => {
    if (!isOpen || !issue?.ID) {
      setIsEditing(false);
      setIncidentDetail(null);
      return;
    }

    let isMounted = true;
    setIsEditing(false);
    setIncidentDetail(issue);

    const fetchIncidentDetail = async () => {
      try {
        const detail = await getIncidentById(issue.ID);
        if (isMounted) {
          setIncidentDetail(detail);
        }
      } catch (error) {
        console.error('Failed to load incident detail for edit modal:', error);
        if (isMounted) {
          setIncidentDetail(issue);
        }
      }
    };

    fetchIncidentDetail();

    return () => {
      isMounted = false;
    };
  }, [isOpen, issue]);

  const currentIncident = incidentDetail || issue;

  useEffect(() => {
    if (isOpen && currentIncident && !isEditing) {
      setFormData(defaultEditForm(currentIncident));
    }
  }, [isOpen, currentIncident, isEditing]);

  const handleEditClick = () => {
    if (!currentIncident) return;
    setFormData(defaultEditForm(currentIncident));
    setIsEditing(true);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, 0);
  };

  const availableCostBearerOptions = useMemo(() => {
    if (!currentIncident) return costBearerOptions;

    return costBearerOptions.filter((opt) => {
      if (opt.value === 'DASH_ELECTRIC') return true;
      if (opt.value === 'MITRA')
        return !!(currentIncident.driverName || currentIncident.driverCode);
      if (opt.value === 'CLIENT')
        return !!(currentIncident.clientName || currentIncident.clientID);
      if (opt.value === 'OEM')
        return !!(currentIncident.oemName || currentIncident.oemID);
      return true;
    });
  }, [currentIncident]);

  // cost bearer detail label/value
  const costBearerDetail = useMemo(() => {
    if (
      !formData.costBearer ||
      formData.costBearer === 'DASH_ELECTRIC' ||
      !currentIncident
    )
      return null;
    const map: Record<string, { label: string; value: string | undefined | null }> = {
      MITRA: {
        label: 'Mitra Name',
        value: `${currentIncident.driverCode || ''} - ${
          currentIncident.driverName || 'No Name'
        }`,
      },
      CLIENT: {
        label: 'Client Name',
        value: currentIncident.clientName,
      },
      OEM: {
        label: 'OEM Name',
        value: currentIncident.oemName,
      },
    };
    return map[formData.costBearer];
  }, [formData.costBearer, currentIncident]);

  // ── save handler ──
  const handleSave = async () => {
    if (!currentIncident) return;
    setIsSaving(true);
    try {
      let imageURL = formData.imageURL;
      let policeReportURL = formData.policeReportURL;

      // Handle file only if it's a new file (not our mock)
      if (
        formData.photos.length > 0 &&
        formData.photos[0].file.name !== EXISTING_IMAGE_FILE_NAME
      ) {
        if (!formData.photos[0].url) {
          throw new Error('Please wait for the image upload to complete.');
        }
        imageURL = formData.photos[0].url;
      }

      if (
        formData.policeReports.length > 0 &&
        formData.policeReports[0].file.name !== EXISTING_POLICE_REPORT_FILE_NAME
      ) {
        if (!formData.policeReports[0].url) {
          throw new Error('Please wait for the police report upload to complete.');
        }
        policeReportURL = formData.policeReports[0].url;
      }

      const userEmail = getUserEmail();
      if (!userEmail) throw new Error('User email not found');

      const payload: UpdateIncidentRequest = {
        costResponsibility: formData.costBearer,
        severity: formData.severity,
        chronology: formData.chronology,
        damageImageURL: imageURL,
        policeReportURL: policeReportURL || undefined,
        updatedBy: userEmail,
        incidentDate: formData.incidentDate,
        driverID: currentIncident.driverID
          ? Number(currentIncident.driverID)
          : undefined,
        driverCode: currentIncident.driverCode || undefined,
        driverName: currentIncident.driverName || undefined,
        clientID:
          formData.costBearer === 'CLIENT' ? currentIncident.clientID : undefined,
        clientName: currentIncident.clientName || undefined,
        oemID: formData.costBearer === 'OEM' ? currentIncident.oemID : undefined,
      };

      await updateIncident(currentIncident.ID, payload);

      showSuccessToast(
        'Success',
        'Incident Report Updated - You can now track the updated progress!'
      );
      handleModalClose();
      onSuccess?.();
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        'Failed to update Incident Report - please try again'
      );
      showErrorToast('Error', message);
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid =
    !!formData.incidentDate &&
    !!formData.severity &&
    !!formData.costBearer &&
    !!formData.chronology.trim() &&
    formData.photos.length > 0;

  const normalizeSeverity = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '-';

  if (!isOpen || !currentIncident) return null;

  const costBearerRaw = currentIncident.costResponsibility;
  const costBearerUpper = costBearerRaw?.toUpperCase();
  const costBearerLabel =
    costBearerOptions.find((o) => o.value === costBearerUpper)?.label ||
    costBearerRaw;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleModalClose}
      position="right"
      width="sm"
      contentRef={scrollRef}
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Incident Report' : 'Edit Incident Report'}
          </h2>
          <p className="text-sm text-gray-600">
            {isEditing ? 'Update Incident details.' : 'Update Incident details.'}
          </p>
        </div>
      }
      footer={
        isEditing ? (
          <Button
            variant="primary"
            onClick={handleSave}
            className="w-full py-3"
            disabled={!isFormValid || isSaving || formData.photos.some(p => p.uploading)}
          >
            {isSaving ? 'Saving...' : 'Next'}
          </Button>
        ) : (
          <button
            className="w-full py-3 rounded-md font-medium text-sm transition-colors"
            onClick={handleEditClick}
            style={{ backgroundColor: '#784DEF1A', color: '#5E2AAC', border: 'none' }}
          >
            Edit
          </button>
        )
      }
    >
      <div className="flex-1">
        {!isEditing ? (
          /* ── VIEW MODE ─────────────────────────────────────── */
          <>
            {/* Vehicle header */}
            <div className="p-6 pb-0">
              <FieldDisplay
                label="Plate Number"
                value={currentIncident.platNomor}
                className="mb-2"
              />
              <div className="grid grid-cols-2 gap-4">
                <FieldDisplay
                  label="OEM Name"
                  value={currentIncident.oemName || '-'}
                  hasBorder={false}
                />
                <FieldDisplay
                  label="Model Name"
                  value={currentIncident.modelName || '-'}
                  hasBorder={false}
                />
              </div>
            </div>

            <SectionHeader title="Incident Detail" />

            <div className="p-6 pt-0 pb-0">
              {/* Incident Type + Severity (side by side) */}
              <div className="pb-2 mb-2">
                <div className="grid grid-cols-2 gap-4 pb-3 border-b border-gray-200">
                  <div>
                    <label className="block mb-1 heading-field">
                      Incident Type
                    </label>
                    <div className="text-sm font-medium text-gray-900 break-words">
                      {INCIDENT_TYPE_MAP[currentIncident.incidentType] ||
                        currentIncident.incidentType}
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1 heading-field">Severity</label>
                    <div className="text-sm font-medium text-gray-900 break-words">
                      {normalizeSeverity(currentIncident.severity)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tanggal Kejadian */}
              <FieldDisplay
                label="Tanggal Kejadian"
                value={
                  currentIncident.incidentDate
                    ? dayjs(currentIncident.incidentDate).format(
                        'DD/MM/YYYY HH:mm:00'
                      )
                    : '-'
                }
                className="mb-2"
              />

              {/* Chronology */}
              <FieldDisplay
                label="Chronology / Description"
                value={
                  currentIncident.chronology || currentIncident.notes || '-'
                }
                className="mb-2"
              />

              {/* Foto Kerusakan */}
              <div className="pb-2 border-b border-gray-200 mb-2">
                <label className="block mb-1 heading-field">
                  Foto Kerusakan Kendaraan
                </label>
                {currentIncident.imageURL || currentIncident.damageImageURL ? (
                  <div className="rounded-lg overflow-hidden mb-3 border border-gray-100">
                    <img
                      src={
                        currentIncident.imageURL ||
                        currentIncident.damageImageURL
                      }
                      alt="Foto Kerusakan"
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center mb-3 text-gray-400">
                    <i className="ri-image-line text-2xl block mb-1"></i>
                    <p className="text-xs">No photo uploaded</p>
                  </div>
                )}
              </div>

              {/* Laporan Polisi */}
              <div className="pb-2 border-b border-gray-200 mb-2">
                <label className="block mb-1 heading-field">Laporan Polisi</label>
                {currentIncident.policeReportURL ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-100 h-44 mb-3 flex items-center justify-center bg-gray-50">
                    <div className="absolute inset-0 p-6 opacity-30 flex flex-col gap-3 overflow-hidden blur-[3px]">
                      <div className="w-1/3 h-4 bg-gray-400 rounded" />
                      <div className="w-full h-2.5 bg-gray-300 rounded" />
                      <div className="w-5/6 h-2.5 bg-gray-300 rounded" />
                      <div className="w-full h-2.5 bg-gray-300 rounded" />
                      <div className="w-3/4 h-2.5 bg-gray-300 rounded" />
                      <div className="w-1/4 h-4 bg-gray-400 rounded mt-4" />
                      <div className="w-full h-2.5 bg-gray-300 rounded" />
                      <div className="w-11/12 h-2.5 bg-gray-300 rounded" />
                      <div className="w-4/5 h-2.5 bg-gray-300 rounded" />
                    </div>
                    <a
                      href={currentIncident.policeReportURL}
                      target="_blank"
                      rel="noreferrer"
                      className="relative z-10 px-5 py-2 bg-white bg-opacity-100 border border-gray-200 rounded-full text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition"
                    >
                      View PDF
                    </a>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center mb-3 text-gray-400">
                    <i className="ri-file-line text-2xl block mb-1"></i>
                    <p className="text-xs">No file uploaded</p>
                  </div>
                )}
              </div>

              {/* Biaya Ditanggung + detail */}
              <div className="grid grid-cols-2 gap-4">
                <FieldDisplay
                  label="Biaya Ditanggung"
                  value={costBearerLabel || '-'}
                  hasBorder={false}
                />
                {costBearerUpper === 'MITRA' && (
                  <FieldDisplay
                    label="Mitra Name"
                    value={`${currentIncident.driverCode || ''} - ${
                      currentIncident.driverName || '-'
                    }`}
                    hasBorder={false}
                  />
                )}
                {costBearerUpper === 'CLIENT' && (
                  <FieldDisplay
                    label="Client Name"
                    value={currentIncident.clientName || '-'}
                    hasBorder={false}
                  />
                )}
                {costBearerUpper === 'OEM' && (
                  <FieldDisplay
                    label="OEM Name"
                    value={currentIncident.oemName || '-'}
                    hasBorder={false}
                  />
                )}
              </div>
            </div>

            <InfoSection issue={currentIncident} />
          </>
        ) : (
          /* ── EDIT MODE ─────────────────────────────────────── */
          <>
            <div className="bg-gray-50 px-6 py-2 text-xs font-medium uppercase tracking-wider border-b border-gray-200 heading-field">
              Incident Detail
            </div>
            <div className="p-6 pb-40 space-y-4">
              {/* Plate Number — locked */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plate Number <span className="text-[#5E2AAC]">*</span>
                </label>
                <SearchableDropdown
                  options={[
                    {
                      value: currentIncident.handoverID || 'CURRENT',
                      label: `${currentIncident.platNomor} - ${currentIncident.oemName} - ${currentIncident.modelName}`,
                    },
                  ]}
                  value={currentIncident.handoverID || 'CURRENT'}
                  onChange={() => {}}
                  disabled
                  className="w-full"
                />
              </div>

              {/* Issue Type — locked */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Type <span className="text-[#5E2AAC]">*</span>
                </label>
                <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-500">
                  {INCIDENT_TYPE_MAP[currentIncident.incidentType] ||
                    currentIncident.incidentType}
                </div>
              </div>

              {/* Tanggal Kejadian */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Kejadian <span className="text-[#5E2AAC]">*</span>
                </label>
                <DatePicker
                  value={formData.incidentDate}
                  onChange={(value) =>
                    setFormData((p) => ({ ...p, incidentDate: value }))
                  }
                  showTimeSelect={true}
                  placeholder="e.g. 01/01/2026 15:30"
                  className="w-full"
                />
              </div>

              {/* Severity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity <span className="text-[#5E2AAC]">*</span>
                </label>
                <SearchableDropdown
                  options={severityOptions}
                  value={formData.severity}
                  onChange={(val) =>
                    setFormData((p) => ({
                      ...p,
                      severity: val as IncidentSeverity,
                    }))
                  }
                  placeholder="e.g. Medium"
                  className="w-full"
                />
              </div>

              {/* Chronology */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chronology / Description <span className="text-[#5E2AAC]">*</span>
                </label>
                <div className="relative">
                  <textarea
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                    rows={4}
                    maxLength={200}
                    value={formData.chronology}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, chronology: e.target.value }))
                    }
                  />
                  <div className="absolute bottom-3 right-2 text-xs text-gray-400">
                    {formData.chronology.length}/200 <i className="ri-draggable" />
                  </div>
                </div>
              </div>

              {/* Foto Kerusakan Kendaraan */}
              <div>
                <FileUploadField
                  label={
                    <span className="text-gray-700 text-sm font-medium">
                      Foto Kerusakan Kendaraan <span className="text-[#5E2AAC]">*</span>
                    </span> as any
                  }
                  accept="image/*"
                  files={formData.photos}
                  onFilesChange={(files) =>
                    setFormData((p) => ({
                      ...p,
                      photos: files,
                      imageURL: files.length > 0 ? p.imageURL : '',
                    }))
                  }
                  uploadFn={async (file: File) => {
                    const uuid = uuidv4();
                    const bucketRes = await getBucketUrlImageV2({
                      uuid,
                      type: INCIDENT_IMAGE_TYPE,
                    });
                    const url = bucketRes.data;
                    const base64 = await new Promise<string>((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(file);
                      reader.onload = () => resolve(reader.result as string);
                      reader.onerror = reject;
                    });
                    const uploadRes = await uploadFileImage(
                      url,
                      base64.split(',')[1],
                      file.name
                    );
                    if (uploadRes.status === 200) {
                      showSuccessToast('Success', 'Foto kerusakan kendaraan berhasil diupload');
                      return url;
                    }
                    throw new Error('Image upload failed');
                  }}
                />
              </div>

              {/* Laporan Polisi (PDF) */}
              <div>
                <FileUploadField
                  label={
                    <span className="text-gray-700 text-sm font-medium">
                      Laporan Polisi (PDF)
                    </span> as any
                  }
                  accept=".pdf,application/pdf"
                  files={formData.policeReports}
                  onFilesChange={(files) =>
                    setFormData((p) => ({
                      ...p,
                      policeReports: files,
                      policeReportURL: files.length > 0 ? p.policeReportURL : '',
                    }))
                  }
                  uploadFn={async (file: File) => {
                    const uuid = uuidv4();
                    const bucketRes = await getBucketUrlFile({
                      uuid,
                      fileType: 'issue', // Used "issue" originally here
                      fileFormat: file.name.split('.').pop() || 'pdf',
                    });
                    const url = bucketRes.data;
                    const base64 = await new Promise<string>((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(file);
                      reader.onload = () => resolve(reader.result as string);
                      reader.onerror = reject;
                    });
                    const uploadRes = await uploadTempFile(
                      url,
                      base64.split(',')[1],
                      file.name,
                      file.type
                    );
                    if (uploadRes.status === 200) {
                      showSuccessToast('Success', 'Laporan polisi berhasil diupload');
                      return url;
                    }
                    throw new Error('Police report upload failed');
                  }}
                />
              </div>

              {/* Biaya Ditanggung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Biaya Ditanggung <span className="text-[#5E2AAC]">*</span>
                </label>
                <SearchableDropdown
                  options={availableCostBearerOptions}
                  value={formData.costBearer}
                  onChange={() => {}}
                  disabled
                  placeholder="e.g. Mitra"
                  className="w-full"
                />
              </div>

              {/* Cost bearer detail */}
              {costBearerDetail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {costBearerDetail.label} <span className="text-[#5E2AAC]">*</span>
                  </label>
                  <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-400 font-medium">
                    {costBearerDetail.value || '-'}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SideBarModal>
  );
};
